/**
 * Sprint 1 (2026-02-11) — Anonymous auth-call gate.
 *
 * `AuthContext` and `CandidateAuthContext` used to fire `/api/auth/me`
 * and `/api/candidates/me` on EVERY initial mount, for every visitor,
 * including truly-anonymous marketing-page traffic. Those calls sit
 * on the critical-render path (they block the loading flag), spam
 * expected-401s into the console, and were flagged by mobile
 * PageSpeed as a Sprint-1 fix.
 *
 * SESSION COOKIES ARE HTTP-ONLY — the client cannot read them from
 * `document.cookie`. We therefore mirror auth status into a tiny,
 * NON-SENSITIVE localStorage marker on login/logout and short-circuit
 * the `/me` call for callers whose marker says "anon".
 *
 * Migration path for prod users who already have a session cookie
 * but no marker yet: their first mount sees `UNKNOWN` and still
 * calls `/me` once — that call sets the marker for every future
 * mount. Zero-regression for existing signed-in users. Anonymous
 * first-time visitors skip the network altogether on every load
 * after the marker is set.
 */

const MARKER_KEY = "bmi_auth_status";

export const AUTH_STATUS = Object.freeze({
    AUTHED: "authed",     // has a valid session (backed by an HttpOnly cookie)
    ANON: "anon",         // definitely logged out (fresh visit or after logout)
    UNKNOWN: "unknown",   // no marker yet — first visit / pre-migration user
});

export function getAuthStatus() {
    try {
        const v = localStorage.getItem(MARKER_KEY);
        return v === AUTH_STATUS.AUTHED || v === AUTH_STATUS.ANON
            ? v
            : AUTH_STATUS.UNKNOWN;
    } catch {
        // Private-mode / SSR — treat as unknown so we don't silently
        // lock a user out; the /me call will still run at worst once.
        return AUTH_STATUS.UNKNOWN;
    }
}

/** Call after any successful login / register / session-refresh. */
export function markAuthed() {
    try { localStorage.setItem(MARKER_KEY, AUTH_STATUS.AUTHED); } catch { /* silent */ }
}

/** Call after logout OR when `/me` returns 401. */
export function markAnon() {
    try { localStorage.setItem(MARKER_KEY, AUTH_STATUS.ANON); } catch { /* silent */ }
}

/**
 * True IFF we should fire `/me` on initial mount. UNKNOWN and AUTHED
 * both fire (UNKNOWN for the migration case). Only ANON short-circuits.
 */
export function shouldCheckSession() {
    return getAuthStatus() !== AUTH_STATUS.ANON;
}
